import { Button, Input } from '@chain-zen/ui'

import type { TActiveAssetType } from '../../hooks/use-eld-asset-selection'

interface IAssetNumberInputProps {
  assetType: TActiveAssetType
  confirmingAssetType?: TActiveAssetType | null
  currentValue?: string | null
  inputValue: string
  isConfirming?: boolean
  isLoadingUpdate?: boolean
  onInputChange: (value: string) => void
  onRemove: () => void
  onSubmit: () => void
}

/**
 * Input component for entering trailer or truck numbers manually.
 * Displays current value and handles Add/Remove/Replace actions.
 */
export const AssetNumberInput = ({
  assetType,
  confirmingAssetType,
  currentValue,
  inputValue,
  isConfirming = false,
  isLoadingUpdate = false,
  onInputChange,
  onRemove,
  onSubmit
}: IAssetNumberInputProps) => {
  const trimmedInput = inputValue.trim()
  const hasCurrentValue = !!currentValue
  const inputMatchesCurrent = hasCurrentValue && trimmedInput === currentValue
  const inputIsEmpty = !trimmedInput
  const inputIsDifferent = hasCurrentValue && trimmedInput && trimmedInput !== currentValue

  const getButtonAction = (): string => {
    if (inputMatchesCurrent) return 'remove'
    if (inputIsDifferent) return 'replace'
    return 'add'
  }
  const buttonAction = getButtonAction()
  const ACTION_LABELS: Record<string, string> = {
    add: 'Add',
    remove: 'Remove',
    replace: 'Replace'
  }
  const isConfirmingThis = isLoadingUpdate && confirmingAssetType === assetType
  const buttonLabel = isConfirmingThis ? 'Saving...' : (ACTION_LABELS[buttonAction] ?? 'Add')

  const handleButtonClick = () => {
    if (buttonAction === 'remove') {
      onRemove()
    } else {
      onSubmit()
    }
  }

  const isButtonDisabled =
    (isLoadingUpdate && confirmingAssetType === assetType) || (buttonAction !== 'remove' && inputIsEmpty)

  const isInputDisabled = isConfirming && confirmingAssetType !== assetType

  const icon = assetType === 'trailer' ? '🚛' : '🚚'
  const label = assetType === 'trailer' ? 'Trailer' : 'Truck'

  return (
    <div className="p-3 bg-muted/50 border border-border rounded-lg">
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
        <span>{icon}</span>
        <span>{label} Number</span>
        {!!currentValue && <span className="text-emerald-400 text-xs">({currentValue})</span>}
      </div>
      <div className="flex gap-2">
        <Input
          type="text"
          placeholder={`Enter ${assetType} number...`}
          value={inputValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && !isButtonDisabled) {
              handleButtonClick()
            }
          }}
          className="flex-1"
          disabled={isInputDisabled}
        />
        <Button
          onClick={handleButtonClick}
          disabled={isButtonDisabled || isInputDisabled}
          variant={buttonAction === 'remove' ? 'destructive' : 'default'}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  )
}
